import { useCallback, useState } from 'react'

/**
 * Estado de um formulário de cadastro, com a regra de erro do styleguide (03 · Campo): o erro nasce
 * ao sair do campo, nunca a cada tecla nem antes de a pessoa digitar; ao tentar salvar, aparecem
 * todos; o que a API recusa (422) fica no campo até ele ser alterado.
 */

export type FormErrors<T> = Partial<Record<keyof T, string>>

/** O que um bloco de formulário recebe: valores, erros visíveis e como avisar mudança e saída. */
export interface FormController<T> {
  values: T
  errors: FormErrors<T>
  update(patch: Partial<T>): void
  touch(key: keyof T): void
}

/**
 * Leva o foco ao primeiro campo com erro, depois que a tela o marcou: num cadastro longo, quem
 * clicou em Salvar no cabeçalho precisa ser levado até o que falta.
 */
export function focusFirstInvalid(container: HTMLElement | null) {
  window.setTimeout(() => {
    container?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  }, 0)
}

export function useFormState<T extends object>(
  initial: () => T,
  validate: (values: T) => FormErrors<T>,
) {
  const [values, setValues] = useState(initial)
  const [touched, setTouched] = useState<ReadonlySet<keyof T>>(() => new Set())
  const [refused, setRefused] = useState<FormErrors<T>>({})
  const [dirty, setDirty] = useState(false)

  const found = validate(values)
  const errors: FormErrors<T> = {}
  for (const key of touched) {
    if (found[key]) errors[key] = found[key]
  }
  for (const key of Object.keys(refused) as (keyof T)[]) {
    if (!errors[key]) errors[key] = refused[key]
  }

  const update = useCallback((patch: Partial<T>) => {
    setValues((current) => ({ ...current, ...patch }))
    setDirty(true)
    setRefused((current) => {
      const next = { ...current }
      for (const key of Object.keys(patch) as (keyof T)[]) delete next[key]
      return next
    })
  }, [])

  const touch = useCallback((key: keyof T) => {
    setTouched((current) => (current.has(key) ? current : new Set(current).add(key)))
  }, [])

  /** Tentativa de salvar: mostra os erros destes campos e diz se dá para seguir. */
  function check(keys: readonly (keyof T)[]): boolean {
    setTouched((current) => new Set([...current, ...keys]))
    return keys.every((key) => !found[key])
  }

  return {
    values,
    errors,
    update,
    touch,
    check,
    /** Os erros por campo que a API devolveu. */
    refuse: setRefused,
    dirty,
    markSaved: () => setDirty(false),
  }
}
